import { useMemo, useState } from "react";
import { Form, message, Modal, Select } from "antd";
import SimpleTableTemplate from "@/components/Templates/SimpleTableTemplate";
import { useUser } from "@/contexts/UserContext";
import { useSignals } from "@/contexts/SignalsContext";
import { useAlertsQuery } from "@/hooks/useAlertsQuery";
import { getUniqueOptionsFromField } from "@/views/Risk/shared/utils";
import {
  useOpenRecordFromParam,
  useSelectableTableView,
} from "@/utils/tableHelpers";
import { createSignalColumns } from "@/views/Risk/Signals/signalsTableConfig";
import SignalsDrawer from "@/views/Risk/Signals/SignalsDrawer";
import type { SignalTableRow } from "@/views/Risk/Signals/utils";
import { isArchived } from "@/components/Insights/utils";

export default function Signals() {
  const { userInfo } = useUser();
  const { signals, addSignal } = useSignals();
  const { alerts, isLoading: isLoadingAlerts } = useAlertsQuery();
  const { selectedRecord, setSelectedRecord, getClickableRowProps } =
    useSelectableTableView<SignalTableRow>();
  const columns = useMemo(() => createSignalColumns(signals), [signals]);
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();

  const alertOptions = useMemo(
    () =>
      alerts.map((alert: any) => ({
        label: alert.title,
        value: alert.id,
      })),
    [alerts],
  );

  const assigneeOptions = useMemo(
    () => getUniqueOptionsFromField({ data: signals, field: "assignee" }),
    [signals],
  );

  const ownersOptions = useMemo(
    () => getUniqueOptionsFromField({ data: signals, field: "owners" }),
    [signals],
  );

  const onSubmit = ({
    alertId,
    assignee,
    owners,
  }: {
    alertId?: string;
    assignee?: string;
    owners?: string;
  }) => {
    const selectedAlert = alerts.find((alert: any) => alert.id === alertId);

    if (!selectedAlert) {
      message.error("Please select an alert.");
      return;
    }

    addSignal({
      signal: selectedAlert.title ?? "",
      category: "KRI",
      current_score: "",
      band: "Green",
      current_status: "G",
      breach_status: isArchived(selectedAlert),
      high_threshold: selectedAlert.condition ?? "",
      owners: owners ?? "",
      assignee: assignee ?? "",
      created_by:
        `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`.trim(),
      comments_governance_log: selectedAlert.condition_met_detail ?? "",
    });

    setOpenModal(false);
    form.resetFields();
  };

  useOpenRecordFromParam({
    paramName: "openSignal",
    rows: signals,
    idField: "signal_id",
    setSelectedRecord: (record) => setSelectedRecord(record),
  });

  return (
    <div className="flex w-full min-w-0 flex-col p-4">
      <SimpleTableTemplate
        title="Signals"
        tooltipText="Review risk signals, their current scores and statuses, linked risks and breaches, and ownership details."
        fileName="signals"
        data={signals}
        columns={columns}
        rowKey="signal_id"
        onRow={getClickableRowProps}
        onAddRecord={() => setOpenModal(true)}
      />
      <SignalsDrawer
        records={signals}
        selectedRecord={selectedRecord}
        setSelectedRecord={setSelectedRecord}
      />
      <Modal
        title="Add a new entry"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        okText="Create"
        cancelText="Cancel"
        okButtonProps={{ autoFocus: true, htmlType: "submit" }}
        destroyOnHidden
        modalRender={(dom) => (
          <Form
            layout="vertical"
            form={form}
            name="signal_form_in_modal"
            clearOnDestroy
            onFinish={onSubmit}
          >
            {dom}
          </Form>
        )}
      >
        <Form.Item
          label="Signal"
          name="alertId"
          rules={[{ required: true, message: "Select an alert." }]}
        >
          <Select
            placeholder="Select a signal"
            options={alertOptions}
            loading={isLoadingAlerts}
            showSearch
            optionLabelProp="label"
          />
        </Form.Item>
        <Form.Item label="Assignee" name="assignee">
          <Select
            placeholder="Select an assignee"
            options={assigneeOptions}
            showSearch
            optionLabelProp="label"
          />
        </Form.Item>
        <Form.Item label="Owners" name="owners">
          <Select
            placeholder="Select owners"
            options={ownersOptions}
            showSearch
            optionLabelProp="label"
          />
        </Form.Item>
      </Modal>
    </div>
  );
}
